/**
 * EntityDetailModal - Base component for Vehicle/Driver detail views
 *
 * Features:
 * - Configurable tabs with icons
 * - Global accordion state management (ALL CLOSED by default)
 * - Customizable color schemes
 * - Responsive modal sizing
 * - Shared data fetching patterns
 *
 * @example
 * <EntityDetailModal
 *   isOpen={true}
 *   onClose={handleClose}
 *   entity={vehicle}
 *   entityType="vehicle"
 *   tabs={VEHICLE_TABS}
 *   colorScheme="amber"
 *   headerTitle="Peugeot 308"
 *   headerSubtitle="AB-123-CD"
 * />
 */
import React, { useState, useCallback, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useAccordionState } from '../../hooks/useAccordionState.js';

// Color schemes for modal headers
const COLOR_SCHEMES = {
  amber: {
    gradient: 'from-amber-500 to-amber-600',
    textMuted: 'text-amber-100',
    tabActive: 'text-amber-600 border-amber-600',
    tabHover: 'hover:text-amber-700'
  },
  blue: {
    gradient: 'from-blue-500 to-blue-600',
    textMuted: 'text-blue-100',
    tabActive: 'text-blue-600 border-blue-600',
    tabHover: 'hover:text-blue-700'
  },
  emerald: {
    gradient: 'from-emerald-500 to-emerald-600',
    textMuted: 'text-emerald-100',
    tabActive: 'text-emerald-600 border-emerald-600',
    tabHover: 'hover:text-emerald-700'
  },
  purple: {
    gradient: 'from-purple-500 to-purple-600',
    textMuted: 'text-purple-100',
    tabActive: 'text-purple-600 border-purple-600',
    tabHover: 'hover:text-purple-700'
  }
};

// Context for sharing accordion state and entity data with tab components
const EntityDetailContext = createContext(null);

export function useEntityDetailContext() {
  const context = useContext(EntityDetailContext);
  if (!context) {
    throw new Error('useEntityDetailContext must be used within EntityDetailModal');
  }
  return context;
}

export function EntityDetailModal({
  isOpen,
  onClose,
  onUpdate,
  onMessage,
  entity,
  entityType, // 'vehicle' | 'driver'
  tabs = [],
  colorScheme = 'amber',
  headerTitle,
  headerSubtitle,
  headerContent, // Custom header slot
  initialTab = null,
  children // Additional modal content (e.g., assignment modals)
}) {
  // Active tab state
  const [activeTab, setActiveTab] = useState(initialTab || (tabs[0]?.id || 'overview'));

  // Global accordion state - ALL CLOSED by default
  const accordionState = useAccordionState({ defaultExpanded: [] });

  // Get color scheme
  const scheme = COLOR_SCHEMES[colorScheme] || COLOR_SCHEMES.amber;

  // Handle tab change - collapse all accordions when switching tabs
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    accordionState.collapseAll();
  }, [accordionState]);

  // Handle entity update - refresh data and notify parent
  const handleUpdate = useCallback(() => {
    onUpdate?.();
  }, [onUpdate]);

  // Context value for child components
  const contextValue = {
    entity,
    entityType,
    accordionState,
    onMessage,
    onUpdate: handleUpdate
  };

  if (!isOpen || !entity) return null;

  // Find active tab config
  const activeTabConfig = tabs.find(t => t.id === activeTab);
  const TabComponent = activeTabConfig?.component;

  return (
    <EntityDetailContext.Provider value={contextValue}>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className={`bg-gradient-to-r ${scheme.gradient} text-white px-6 py-4`}>
            <div className="flex justify-between items-center">
              <div>
                {headerContent || (
                  <>
                    <h2 className="text-2xl font-bold">{headerTitle}</h2>
                    {headerSubtitle && (
                      <p className={scheme.textMuted}>{headerSubtitle}</p>
                    )}
                  </>
                )}
              </div>
              <button onClick={onClose} className="text-white hover:text-gray-200">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          {tabs.length > 0 && (
            <div className="bg-gray-50 px-6 py-2 border-b border-gray-200">
              <div className="flex space-x-1 overflow-x-auto">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`px-4 py-2 rounded-t-lg font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
                        isActive
                          ? `bg-white ${scheme.tabActive} border-b-2`
                          : `text-gray-600 ${scheme.tabHover}`
                      }`}
                    >
                      {Icon && <Icon className="w-4 h-4" />}
                      {tab.label}
                      {typeof tab.badge === 'number' && tab.badge > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-200 rounded-full">
                          {tab.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Content - Fixed height with scroll to prevent yo-yo effect when switching tabs */}
          <div className="h-[65vh] overflow-y-auto p-6">
            {TabComponent && (
              <TabComponent
                entity={entity}
                entityType={entityType}
                onMessage={onMessage}
                onUpdate={handleUpdate}
                {...(activeTabConfig.props || {})}
              />
            )}
          </div>
        </div>
      </div>

      {/* Additional modals passed as children */}
      {children}
    </EntityDetailContext.Provider>
  );
}

/**
 * EntityDetailSection - Consistent section styling within tabs
 */
export function EntityDetailSection({ title, children, className = '' }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-6 ${className}`}>
      {title && (
        <h3 className="text-lg font-bold text-gray-900 mb-4">{title}</h3>
      )}
      {children}
    </div>
  );
}

/**
 * EntityInfoGrid - Grid layout for entity information fields
 */
export function EntityInfoGrid({ fields, columns = 3 }) {
  const { t } = useTranslation();
  const gridCols = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4'
  };

  return (
    <div className={`grid ${gridCols[columns] || gridCols[3]} gap-4`}>
      {fields.map((field, index) => (
        <div key={field.key || index}>
          <p className="text-sm text-gray-500">{field.label}</p>
          {field.render ? (
            field.render(field.value)
          ) : (
            <p className="font-medium text-gray-900">{field.value || t('common.states.notAvailable')}</p>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * EntityStatusBadge - Consistent status badge styling
 */
export function EntityStatusBadge({ status, config }) {
  const statusConfig = config?.[status] || {
    color: 'bg-gray-100 text-gray-800',
    label: status
  };

  return (
    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusConfig.color}`}>
      {statusConfig.label}
    </span>
  );
}

/**
 * AssignmentCard - Card for displaying current assignments (org, driver, location)
 */
export function AssignmentCard({
  title,
  icon: Icon,
  color = 'blue', // blue, green, purple, amber
  children,
  action,
  onAction
}) {
  const colorSchemes = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    purple: 'bg-purple-50 border-purple-200',
    amber: 'bg-amber-50 border-amber-200'
  };

  const iconColors = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    purple: 'text-purple-600',
    amber: 'text-amber-600'
  };

  return (
    <div className={`${colorSchemes[color] || colorSchemes.blue} border rounded-lg p-6`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-5 h-5 ${iconColors[color] || iconColors.blue}`} />}
          <h3 className="font-bold text-gray-900">{title}</h3>
        </div>
        {action && onAction && (
          <button
            onClick={onAction}
            className={`${iconColors[color] || iconColors.blue} hover:opacity-75`}
            title={action.title}
          >
            {action.icon && <action.icon className="w-5 h-5" />}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

/**
 * EmptyState - Consistent empty state display
 */
export function EmptyState({ icon: Icon, title, message }) {
  return (
    <div className="text-center py-12 bg-gray-50 rounded-lg">
      {Icon && <Icon className="w-16 h-16 text-gray-300 mx-auto mb-4" />}
      {title && <h4 className="text-lg font-semibold text-gray-900 mb-2">{title}</h4>}
      {message && <p className="text-gray-600">{message}</p>}
    </div>
  );
}

export default EntityDetailModal;
